/* eslint-disable react/prop-types */
import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ModalBody } from 'reactstrap';
import PropTypes from 'prop-types';

import useApi from 'hooks/useApi';
import { closeAlert, showAlert } from 'redux/actions/alertActions';

import DragDrop from 'shared/components/DragDrop';
import { FileSizeNumber } from 'shared/helpers/fileSize/fileSize';
import { LabelError } from 'shared/components/Labels';
import { ButtonIcon, Button } from 'shared/components/Buttons';
import {
  ALERT_TYPE_ERROR,
  toBase64,
  getIconAccessDocument,
  getIconCode,
  getIconDoc,
  getIconExcel,
  getIconImage,
  getIconPdf,
  getIconPpt,
  getIconSound,
  getIconVideo,
  getIconZip,
} from 'shared/utils';
import { colors } from 'shared/defaultTheme';
import { Switch } from 'shared/components/form/Inputs';
import { ContactsModal } from 'shared/components/SharedStyles/SharedStyles';
import { ModalHeader } from 'shared/components/modal/ModalHeaders';
import AlertModal from 'shared/components/AlertModal';
import ConfirmModal from 'shared/components/ConfirmModal';
import MainSpinner from 'shared/components/MainSpinner';

import { ReactComponent as TrashIcon } from 'assets/icon-trash.svg';
import { ReactComponent as DocumentIcon } from 'assets/icon-document.svg';
import { ReactComponent as PdfIcon } from 'assets/icon-microsoft-pdf.svg';
import { ReactComponent as WordIcon } from 'assets/icon-microsoft-word.svg';
import { ReactComponent as ImgIcon } from 'assets/icon-microsoft-img.svg';
import { ReactComponent as ExcelIcon } from 'assets/icon-microsoft-excel.svg';
import { ReactComponent as HtmlIcon } from 'assets/icon-microsoft-html.svg';
import { ReactComponent as VideoIcon } from 'assets/icon-microsoft-video.svg';
import { ReactComponent as PowerPointIcon } from 'assets/icon-microsoft-ppt.svg';
import { ReactComponent as ZipIcon } from 'assets/icon-microsoft-zip.svg';
import { ReactComponent as FolderIcon } from 'assets/icon-microsoft-folder-closed.svg';

import ShareDocumentContacts from './ShareDocumentContacts';
import SelectCustomFolder from './SelectCustomFolder';
import {
  UploadDocumentClientsWrapper,
  ScrollAttach,
  DivFileNameAttachForm,
  SelectCustomFolderModal,
  SelectedFolderWrapper,
} from './styles';

const DEFAULT_PARENT_PATH_ID = '1/2/';

const UploadDocumentClients = ({ setSelectedClients, selectedClients }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { customAreaApi } = useApi();
  const { cusNavId, mail, name, surname } = useSelector((state) => state.auth);
  const { currentClient } = useSelector((state) => state.clientState);
  const { openAlert, alertType, alertMessage } = useSelector((state) => state.alertState);
  const [uploadedError, setUploadedError] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [activeFileAll, setActiveFileAll] = useState(true);
  const [openContactsModal, setOpenContactsModal] = useState(false);
  const [isSelectCustomFolderOpen, setIsSelectCustomFolderOpen] = useState(false);
  const [folderSelected, setFolderSelected] = useState(null);
  const [showError, setShowError] = useState(false);
  const [messageError, setMessageError] = useState('');
  const [showWaiting, setShowWaiting] = useState(false);
  const [WaitingMessage, setWaitingMessage] = useState('');
  const [showSuccessUpload, setShowSuccessUpload] = useState();
  const [showConfirmExistFile, setShowConfirmExistFile] = useState(false);
  const [confirmMessages, setConfirmMessages] = useState([]);
  const [showCancelAlert, setShowCancelAlert] = useState(false);
  const codeOperation = useRef(null);
  const showCancelOperation = useRef(false);

  const [, startOperation] = customAreaApi(
    {
      method: 'POST',
    },
    { manual: true }
  );

  const [, cancelOperation] = customAreaApi(
    {
      method: 'PUT',
    },
    { manual: true }
  );

  const onCancelOperation = async () => {
    setShowWaiting(false);
    showCancelOperation.current = true;
    setShowCancelAlert(true);
    setSelectedFiles([]);
    const {
      data: { data },
    } = await cancelOperation({
      url: `${window.API_V1}/ope/${cusNavId}/${encodeURIComponent(mail)}/${
        codeOperation.current
      }?type=copy`,
    });
    if (data) {
      codeOperation.current = null;
      dispatch(
        showAlert({
          alertType: ALERT_TYPE_ERROR,
          alertMessage: t('custom_field_operation_error_message'),
        })
      );
    }
  };

  const [, createDocument] = customAreaApi(
    {
      method: 'POST',
    },
    { manual: true }
  );

  const [{ loading: loadingCheckItemExists }, getCheckItemExists] = customAreaApi(
    {
      method: 'POST',
    },
    { manual: true }
  );

  const onToggleError = () => setShowError((currentShow) => !currentShow);

  const onErrorLogoUploading = (error) => {
    setUploadedError(error);
  };

  const onCloseAlert = () => {
    dispatch(closeAlert());
  };

  const onCancelAlert = () => setShowCancelAlert((currentCancerlAlert) => !currentCancerlAlert);

  const saveDocumentReducer = async (files) => {
    const newFilesPromises = files.map((file) => {
      return new Promise((resolve) => {
        toBase64(file).then((currentFileBase64) => {
          resolve({
            name: file.name,
            type: `.${file.name.split('.')[1]}`,
            size: file.size,
            permissions: [
              {
                u: mail,
                p: 'rw-',
              },
            ],
            parentPathId: folderSelected?.pathId || DEFAULT_PARENT_PATH_ID,
            pathId: null,
            data: currentFileBase64,
            createdBy: { nameComplete: `${name} ${surname}`, mail },
            created: new Date(),
          });
        });
      });
    });
    Promise.all(newFilesPromises).then((newFiles) => {
      setSelectedFiles([...selectedFiles, ...newFiles]);
    });
  };

  useEffect(() => {
    if (selectedFiles.length > 0) {
      const updatedFiles = selectedFiles.map((file) => {
        if (file.parentPathId !== folderSelected?.pathId) {
          return {
            ...file,
            parentPathId: folderSelected?.pathId ? folderSelected?.pathId : DEFAULT_PARENT_PATH_ID,
          };
        }
        return file;
      });
      setSelectedFiles(updatedFiles);
    }
  }, [folderSelected]);

  const getUploadSize = (files) => {
    let totalSizeUpload = 0;
    files.forEach((file) => {
      const megaBytes = FileSizeNumber(file.size);
      totalSizeUpload += parseInt(megaBytes, 10);
    });
    if (totalSizeUpload > 124) {
      setMessageError(t('new_document_text_maximum_size_upload'));
      setShowError(true);
      return false;
    }
    return true;
  };

  const getFiles = (files) => {
    if (selectedFiles.length > 0) {
      let newFiles = [];
      files.forEach((selectedFile) => {
        const existFile = selectedFiles.some((x) => x.name === selectedFile.name);
        if (!existFile) {
          newFiles = [...newFiles, selectedFile];
        }
      });
      const validateUploadFile = getUploadSize([...newFiles, ...selectedFiles]);
      if (validateUploadFile) {
        saveDocumentReducer(newFiles);
      }
    }

    if (selectedFiles.length === 0) {
      const validateUploadFile = getUploadSize(files);
      if (validateUploadFile) {
        saveDocumentReducer(files);
      }
    }
  };

  const removeFile = (file, index) => {
    const newFiles = selectedFiles.filter((item, i) => i !== index && item.id !== index);
    setSelectedFiles(newFiles);
  };

  const onCloseContactsModal = () => {
    setOpenContactsModal(false);
  };

  const IconFile = ({ file }) => {
    if (getIconDoc(file.type)) {
      return <WordIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconPdf(file.type)) {
      return <PdfIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconImage(file.type)) {
      return <ImgIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconCode(file.type)) {
      return <HtmlIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconSound(file.type)) {
      return <VideoIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconVideo(file.type)) {
      return <VideoIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconAccessDocument(file.type)) {
      return <ExcelIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconPpt(file.type)) {
      return (
        <PowerPointIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />
      );
    }
    if (getIconZip(file.type)) {
      return <ZipIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconExcel(file.type)) {
      return <ExcelIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    return <DocumentIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
  };

  IconFile.propTypes = {
    file: PropTypes.shape({
      type: PropTypes.string,
    }).isRequired,
  };

  const handleCheckItemExists = async (currentClientId) => {
    try {
      const nameFile = selectedFiles.map((x) => {
        return x.name;
      });
      const {
        data: { data },
      } = await getCheckItemExists({
        url: `${
          window.API_V1
        }/cli/items/checkitemexists/${cusNavId}/${currentClientId}/${encodeURIComponent(
          mail
        )}?parentPathId=${folderSelected?.pathId || DEFAULT_PARENT_PATH_ID}`,
        data: nameFile,
      });
      return data;
    } catch (error) {
      return null;
    }
  };

  const onCreateDocument = async () => {
    const {
      data: { data },
    } = await startOperation({
      url: `${window.API_V1}/ope/${cusNavId}/${encodeURIComponent(mail)}?type=copy`,
    });
    codeOperation.current = data.code;
    showCancelOperation.current = false;
    setShowWaiting(true);
    try {
      for (let index = 0; index < selectedClients.length; index += 1) {
        if (showCancelOperation.current) break;
        const selectedClient = selectedClients[index];
        setWaitingMessage(
          t('documents_upload_document_client', {
            value: selectedClient.clientName,
          })
        );
        // eslint-disable-next-line no-await-in-loop
        await createDocument({
          data: selectedFiles,
          url: `${window.API_V1}/cli/items/${cusNavId}/${
            selectedClient.clientId
          }/${encodeURIComponent(mail)}?publish=${true}&codeOperation=${data.code}`,
        });
      }
      setShowWaiting(false);
      setSelectedFiles([]);
      setShowSuccessUpload(!showCancelOperation.current);
    } catch (error) {
      setShowWaiting(false);
      if (!showCancelOperation.current) {
        dispatch(
          showAlert({
            alertType: ALERT_TYPE_ERROR,
            alertMessage: t('custom_field_operation_error_message'),
          })
        );
      }
    }
  };

  const onSaveFiles = async () => {
    let newExistItems = [];
    for (let index = 0; index < selectedClients.length; index += 1) {
      const selectedClient = selectedClients[index];
      // eslint-disable-next-line no-await-in-loop
      const existItems = await handleCheckItemExists(selectedClient.id);
      newExistItems = [...newExistItems, { ...existItems, clientName: selectedClient.clientName }];
    }
    const existFiles = newExistItems.some((x) => x.rename);
    if (existFiles) {
      const filterNamesFinal = newExistItems.filter((x) => x.namesFinal);
      let newNameFile = [];
      for (let index = 0; index < filterNamesFinal.length; index += 1) {
        const nameFinal = filterNamesFinal[index];
        for (let jndex = 0; jndex < selectedFiles.length; jndex += 1) {
          const newUploadFile = selectedFiles[jndex];
          const exitsName = nameFinal.namesFinal[newUploadFile.name];
          if (exitsName) {
            newNameFile = [
              ...newNameFile,
              t('documents_exits_file_messages', {
                interpolation: { escapeValue: false },
                oldName: newUploadFile.name,
                newName: exitsName,
                route: `${nameFinal.clientName}/Buzón`,
              }),
            ];
          }
        }
      }
      setConfirmMessages(newNameFile);
      setShowConfirmExistFile(true);
    } else if (activeFileAll) {
      setOpenContactsModal(true);
    } else {
      onCreateDocument();
    }
  };

  const onOkExistFile = () => {
    setShowConfirmExistFile(false);
    if (activeFileAll) {
      setOpenContactsModal(true);
    } else {
      onCreateDocument();
    }
  };

  const onCancelExistFile = () => {
    setShowConfirmExistFile(false);
  };

  return (
    <UploadDocumentClientsWrapper>
      <h2 className="upload-document-title mb-3">
        {t('documents_Upload_document_mailbox_folder')}
      </h2>
      <div className="upload-document-box">
        <div className="d-flex upload-document-form-container">
          <div className="mb-2 mr-3 upload-document-drag-drop">
            <DragDrop
              onChangeFile={(files) => getFiles(files)}
              onError={onErrorLogoUploading}
              height={280}
              iconWidth={70}
            />
            <Button
              type="button"
              color="primary"
              className="destination-button"
              label={t('documents_upload_destination_folder')}
              onClick={() => setIsSelectCustomFolderOpen(true)}
            />
            {folderSelected && (
              <SelectedFolderWrapper>
                <div className="info-container">
                  <div className="folder-info">
                    <FolderIcon className="folder-icon" />
                    <span>{folderSelected.i18Name}</span>
                  </div>
                  <TrashIcon className="trash-icon" onClick={() => setFolderSelected()} />
                </div>
              </SelectedFolderWrapper>
            )}

            {uploadedError && <LabelError label={uploadedError} />}
          </div>
          <div className="upload-document-files">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h5 className="text-uppercase">{t('general_attach_documents')}</h5>
              <span>{`(${selectedFiles.length})`}</span>
            </div>
            <div className="upload-document-divider" />
            <ScrollAttach>
              {selectedFiles.length > 0 && <hr />}
              {selectedFiles.length > 0
                ? selectedFiles.map((selectedFile, index) => (
                    <div key={selectedFile.name}>
                      <div className="d-flex ml-2">
                        <div>
                          <IconFile file={selectedFile} />
                        </div>
                        <div className="d-flex justify-content-between w-100">
                          <DivFileNameAttachForm>
                            <span className="ml-1">{selectedFile.name}</span>
                          </DivFileNameAttachForm>
                          <div className="d-flex">
                            <ButtonIcon
                              id="delete-document-notification"
                              title={t('documents_delete')}
                              type="button"
                              Icon={() => <TrashIcon fill={colors.suvaGrey} />}
                              onClick={() => removeFile(selectedFile, index)}
                              className="button-icon"
                            />
                          </div>
                        </div>
                      </div>
                      <hr />
                    </div>
                  ))
                : null}
            </ScrollAttach>
            {selectedFiles.length >= 1 && (
              <div className="my-3 d-flex align-items-center justify-content-between">
                <h6 className="upload-document-public-text">{t('documents_public_portal_all')}</h6>
                <div className="mb-n2 pl-3 d-flex align-items-center">
                  <span htmlFor="switch" className="document-all-file-text">
                    {t('documents_all_file')}
                  </span>
                  <Switch
                    className="text-uppercase"
                    id="switchActiveFileAll"
                    onChange={(checked) => {
                      setActiveFileAll(checked);
                    }}
                    value={!activeFileAll}
                    color={activeFileAll ? colors.bilbao : colors.error}
                    classNameMain="ml-1"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="action-container">
          <Button
            className="text-uppercase mr-4 button-action"
            type="button"
            color="secondary"
            label={t('general_button_cancel')}
            onClick={() => {
              setSelectedClients([currentClient]);
            }}
          />
          <Button
            className="text-uppercase button-action mr-4"
            type="button"
            color="primary"
            label={t('general_attachment')}
            onClick={() => {
              const newSelectedClients = selectedClients.map((client) => {
                return {
                  ...client,
                  isActiveAll: true,
                };
              });
              setSelectedClients([...newSelectedClients]);
              onSaveFiles();
            }}
            disabled={!(selectedFiles.length > 0) || selectedClients.length === 0}
          />
        </div>
      </div>
      <ContactsModal isOpen={openContactsModal} toggle={onCloseContactsModal} size="lg" top="1%">
        <ModalHeader
          className="modal-header"
          backgroundColor={colors.transparent}
          onClose={onCloseContactsModal}
        />
        <ModalBody>
          <ShareDocumentContacts
            selectedClients={selectedClients}
            setSelectedClients={setSelectedClients}
            selectedFiles={selectedFiles}
            setSelectedFiles={setSelectedFiles}
            onClose={onCloseContactsModal}
          />
        </ModalBody>
      </ContactsModal>
      <SelectCustomFolderModal
        isOpen={isSelectCustomFolderOpen}
        toggle={() => setIsSelectCustomFolderOpen(false)}
        size="md"
      >
        <ModalHeader
          className="modal-header"
          backgroundColor={colors.transparent}
          onClose={() => setIsSelectCustomFolderOpen(false)}
        />
        <div className="title-wrapper">
          <h1 className="title">{t('documents_upload_destination_folder')}</h1>
        </div>
        <ModalBody>
          <SelectCustomFolder
            setFolderSelected={setFolderSelected}
            folderSelected={folderSelected}
            onClose={() => setIsSelectCustomFolderOpen(false)}
          />
        </ModalBody>
      </SelectCustomFolderModal>
      {loadingCheckItemExists && <MainSpinner />}
      <AlertModal
        type="waiting"
        isOpen={showWaiting}
        toggle={onCancelOperation}
        message={WaitingMessage}
        okText={t('general_button_cancel')}
        onOk={onCancelOperation}
      />
      <AlertModal
        type="success"
        isOpen={showSuccessUpload}
        toggle={() => setShowSuccessUpload(false)}
        message={t('documents_success_upload_file')}
        okText={t('general_button_ok')}
        onOk={() => setShowSuccessUpload(false)}
      />
      <AlertModal
        type="error"
        isOpen={showError}
        toggle={onToggleError}
        message={messageError}
        okText={t('general_button_ok')}
        onOk={onToggleError}
      />
      <ConfirmModal
        isOpen={showConfirmExistFile}
        toggle={onCancelExistFile}
        confirmMessages={confirmMessages}
        okText={t('general_button_ok')}
        onOk={onOkExistFile}
        cancelText={t('general_button_cancel')}
        onCancel={onCancelExistFile}
      />
      <AlertModal
        type={alertType}
        isOpen={openAlert}
        toggle={onCloseAlert}
        message={alertMessage}
        okText={t('general_button_ok')}
        onOk={onCloseAlert}
      />
      <AlertModal
        type="warning"
        isOpen={showCancelAlert}
        toggle={onCancelAlert}
        message={t('documents_canceled_upload')}
        okText={t('general_button_ok')}
        onOk={onCancelAlert}
      />
    </UploadDocumentClientsWrapper>
  );
};

UploadDocumentClients.defaultProps = {
  setSelectedClients: null,
  selectedClients: null,
};

UploadDocumentClients.propTypes = {
  setSelectedClients: PropTypes.func,
  selectedClients: PropTypes.arrayOf(PropTypes.shape({})),
};

export default UploadDocumentClients;
